import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";
import type {
  ISubscriptionPlan,
  ISubscriptionCategory,
  ISubscriptionFaq,
  ITopUpToken,
  ISubscriptionActivateResponse,
  ISubscriptionCurrentPlan,
  ISubscriptionTokenUsage,
  ISubscriptionTokenSummary,
  ISubscriptionInvoice,
  ISubscriptionBillingAddress,
  ISubscriptionPaymentMethod,
  ISubscriptionSetupIntent,
  ISubscriptionTokenBalance,
  ISubscriptionTopUpIntent,
  ISubscriptionUpcomingInvoice,
  ISeatStatus,
  ISeatUpgradeResponse,
  IAiAction,
  ITokenPurchaseHistoryItem,
  ISeatHistoryItem,
  IPaymentHistoryItem,
  IActivityItem,
  IActivityResponseMeta,
  ActivityEventType,
  ITokenUsageBreakdown,
  IUserTimeSaved,
  IUsersTimeSaved,
} from "@/types/(company)/subscription.interface";
import {
  API_GET_SUBSCRIPITON_PLAN,
  API_GET_SUBSCRIPITON_CATEGORIES,
  API_GET_SUBSCRIPTION_FAQS,
  API_GET_TOP_UP_TOKENS,
  API_ACTIVATE_SUBSCRIPTION_PLAN,
  API_SUBSCRIPTION_GET_CURRENT_PLAN,
  API_SUBSCRIPTION_GET_TOKEN_SUMMARY,
  API_SUBSCRIPTION_GET_TOKEN_USAGE,
  API_CANCEL_SUBSCRIPTION_PLAN,
  API_GET_SUBSCRIPTION_PLAN_INVOICES,
  API_GET_SUBSCRIPTION_BILLING_ADDRESS,
  API_UPDATE_SUBSCRIPTION_BILLING_ADDRESS,
  API_ADD_NEW_SUBSCRIPTION_PAYMENT_METHOD,
  API_GET_PAYMENT_METHOD_CARD,
  API_SET_DEFAULT_PAYMENT_METHOD,
  API_REMOVE_SUBSCRIPTION_PAYMENT_METHOD,
  API_GET_SUBSCRIPTION_TOKEN_BALANCE,
  API_CREATE_TOP_UP_PACKAGE,
  API_GET_TOP_UP_PACKAGE_BY_ID,
  API_SUBSCRIPTION_GET_UPCOMING_INVOICE,
  API_RESUME_SUBSCRIPTION_PLAN,
  API_RETRY_SUBSCRIPTION_PAYMENT,
  API_SUBSCRIPTION_SEATS_STATUS,
  API_SUBSCRIPTION_SEATS_UPGRADE,
  API_SUBSCRIPTION_SEATS_SCHEDULE_DOWNGRADE,
  API_SUBSCRIPTION_SEATS_CANCEL_SCHEDULED,
  API_GET_AI_ACTIONS,
  API_SUBSCRIPTION_TOKEN_PURCHASES,
  API_SUBSCRIPTION_SEAT_HISTORY,
  API_SUBSCRIPTION_PAYMENT_HISTORY,
  API_SUBSCRIPTION_ACTIVITY,
  API_SUBSCRIPTION_TOKEN_USAGE_BREAKDOWN,
  API_SUBSCRIPTION_MY_TIME_SAVED,
  API_SUBSCRIPTION_USERS_TIME_SAVED,
  API_SUBSCRIPTION_EXPORT_TOKEN_SUMMARY,
} from "@/constants/apiEndpoints";

export const subscriptionApi = createApi({
  reducerPath: "subscriptionApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  tagTypes: [
    "getSubscriptionPlan",
    "getSubscriptionCategories",
    "getSubscriptionFaqs",
    "getTopUpTokens",
    "getCurrentPlan",
    "getTokenSummary",
    "getTokenUsage",
    "getSubscriptionInvoices",
    "getBillingAddress",
    "getPaymentMethods",
    "getTokenBalance",
    "getTopUpPackageById",
    "getSeatStatus",
    "getActivity",
    "getTokenPurchases",
    "getSeatHistory",
    "getPaymentHistory",
    "getUpcomingInvoice",
    "getTokenUsageBreakdown",
    "getMyTimeSaved",
    "getUsersTimeSaved",
  ],
  endpoints: (builder) => ({
    getSubscriptionPlan: builder.query<IApiResponse<ISubscriptionPlan>, void>({
      query: () => ({
        url: API_GET_SUBSCRIPITON_PLAN,
        method: "GET",
      }),
      providesTags: ["getSubscriptionPlan"],
    }),

    getSubscriptionCategories: builder.query<
      IApiPaginatedResponse<ISubscriptionCategory[]>,
      void
    >({
      query: () => ({
        url: API_GET_SUBSCRIPITON_CATEGORIES,
        method: "GET",
      }),
      providesTags: ["getSubscriptionCategories"],
    }),

    getSubscriptionFaqs: builder.query<
      IApiPaginatedResponse<ISubscriptionFaq>,
      void
    >({
      query: () => ({
        url: API_GET_SUBSCRIPTION_FAQS,
        method: "GET",
      }),
      providesTags: ["getSubscriptionFaqs"],
    }),

    getTopUpTokens: builder.query<IApiPaginatedResponse<ITopUpToken>, void>({
      query: () => ({
        url: API_GET_TOP_UP_TOKENS,
        method: "GET",
      }),
      providesTags: ["getTopUpTokens"],
    }),

    activateSubscription: builder.mutation<
      IApiResponse<ISubscriptionActivateResponse>,
      void
    >({
      query: () => ({
        url: API_ACTIVATE_SUBSCRIPTION_PLAN,
        method: "POST",
      }),
      invalidatesTags: [
        "getCurrentPlan",
        "getActivity",
        "getSeatStatus",
        "getUpcomingInvoice",
        "getTokenBalance",
        "getTokenUsage",
      ],
    }),

    getCurrentPlan: builder.query<IApiResponse<ISubscriptionCurrentPlan>, void>(
      {
        query: () => ({
          url: API_SUBSCRIPTION_GET_CURRENT_PLAN,
          method: "GET",
        }),
        providesTags: ["getCurrentPlan"],
      },
    ),

    getTokenSummary: builder.query<
      IApiPaginatedResponse<ISubscriptionTokenSummary>,
      IApiGeneralParams & { direction?: "credits" | "debits" }
    >({
      query: (params) => ({
        url: API_SUBSCRIPTION_GET_TOKEN_SUMMARY,
        method: "GET",
        params,
      }),
      providesTags: ["getTokenSummary"],
    }),

    exportTokenSummary: builder.query<
      IApiResponse<ISubscriptionTokenSummary[]>,
      { direction?: "credits" | "debits" }
    >({
      query: (params) => ({
        url: API_SUBSCRIPTION_EXPORT_TOKEN_SUMMARY,
        method: "GET",
        params,
      }),
    }),

    getTokenUsage: builder.query<IApiResponse<ISubscriptionTokenUsage>, void>(
      {
        query: () => ({
          url: API_SUBSCRIPTION_GET_TOKEN_USAGE,
          method: "GET",
        }),
        providesTags: ["getTokenUsage"],
      },
    ),

    getUpcomingInvoice: builder.query<
      IApiResponse<ISubscriptionUpcomingInvoice>,
      void
    >({
      query: () => ({
        url: API_SUBSCRIPTION_GET_UPCOMING_INVOICE,
        method: "GET",
      }),
      providesTags: ["getUpcomingInvoice"],
    }),

    cancelSubscription: builder.mutation<IApiResponse<void>, void>({
      query: () => ({
        url: API_CANCEL_SUBSCRIPTION_PLAN,
        method: "DELETE",
      }),
      invalidatesTags: [
        "getCurrentPlan",
        "getActivity",
        "getUpcomingInvoice",
      ],
    }),

    getSubscriptionInvoices: builder.query<
      IApiPaginatedResponse<ISubscriptionInvoice[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: API_GET_SUBSCRIPTION_PLAN_INVOICES,
        method: "GET",
        params,
      }),
      providesTags: ["getSubscriptionInvoices"],
    }),

    // ── Billing Address ────────────────────────────────────────────────────────

    getBillingAddress: builder.query<
      IApiResponse<ISubscriptionBillingAddress>,
      void
    >({
      query: () => ({
        url: API_GET_SUBSCRIPTION_BILLING_ADDRESS,
        method: "GET",
      }),
      providesTags: ["getBillingAddress"],
    }),

    updateBillingAddress: builder.mutation<
      IApiResponse<ISubscriptionBillingAddress>,
      Partial<ISubscriptionBillingAddress>
    >({
      query: (body) => ({
        url: API_UPDATE_SUBSCRIPTION_BILLING_ADDRESS,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["getBillingAddress"],
    }),

    // ── Payment Methods ────────────────────────────────────────────────────────

    createSetupIntent: builder.mutation<
      IApiResponse<ISubscriptionSetupIntent>,
      void
    >({
      query: () => ({
        url: API_ADD_NEW_SUBSCRIPTION_PAYMENT_METHOD,
        method: "POST",
      }),
    }),

    getPaymentMethods: builder.query<
      IApiResponse<ISubscriptionPaymentMethod[]>,
      void
    >({
      query: () => ({
        url: API_GET_PAYMENT_METHOD_CARD,
        method: "GET",
      }),
      providesTags: ["getPaymentMethods"],
    }),

    setDefaultPaymentMethod: builder.mutation<IApiResponse<void>, string>({
      query: (paymentMethodId) => ({
        url: API_SET_DEFAULT_PAYMENT_METHOD(paymentMethodId),
        method: "PUT",
      }),
      invalidatesTags: ["getPaymentMethods"],
    }),

    removePaymentMethod: builder.mutation<IApiResponse<void>, string>({
      query: (paymentMethodId) => ({
        url: API_REMOVE_SUBSCRIPTION_PAYMENT_METHOD(paymentMethodId),
        method: "DELETE",
      }),
      invalidatesTags: ["getPaymentMethods"],
    }),
    // ── Tokens ───────────────────────────────────────────────────────────────

    getTokenBalance: builder.query<
      IApiResponse<ISubscriptionTokenBalance>,
      void
    >({
      query: () => ({
        url: API_GET_SUBSCRIPTION_TOKEN_BALANCE,
        method: "GET",
      }),
      providesTags: ["getTokenBalance"],
    }),

    createTopUpIntent: builder.mutation<
      IApiResponse<ISubscriptionTopUpIntent>,
      { packageId: number }
    >({
      query: (body) => ({
        url: API_CREATE_TOP_UP_PACKAGE,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "getTokenBalance",
        "getTokenUsage",
        "getTokenSummary",
        "getTokenPurchases",
        "getPaymentHistory",
        "getActivity",
        "getSubscriptionInvoices",
      ],
    }),
    getTopUpPackageById: builder.query<IApiResponse<ITopUpToken>, string>({
      query: (packageId) => ({
        url: API_GET_TOP_UP_PACKAGE_BY_ID(packageId),
        method: "GET",
      }),
      providesTags: ["getTopUpPackageById"],
    }),

    resumeSubscription: builder.mutation<void, void>({
      query: () => ({
        url: API_RESUME_SUBSCRIPTION_PLAN,
        method: "POST",
      }),
      invalidatesTags: [
        "getCurrentPlan",
        "getActivity",
        "getUpcomingInvoice",
      ],
    }),

    retryPayment: builder.mutation<
      { clientSecret: string | null; success: boolean },
      void
    >({
      query: () => ({
        url: API_RETRY_SUBSCRIPTION_PAYMENT,
        method: "POST",
      }),
      invalidatesTags: [
        "getCurrentPlan",
        "getActivity",
        "getPaymentHistory",
        "getUpcomingInvoice",
        "getSubscriptionInvoices",
      ],
    }),

    getSeatStatus: builder.query<IApiResponse<ISeatStatus>, void>({
      query: () => ({
        url: API_SUBSCRIPTION_SEATS_STATUS,
        method: "GET",
      }),
      providesTags: ["getSeatStatus"],
    }),

    upgradeSeats: builder.mutation<
      IApiResponse<ISeatUpgradeResponse>,
      { targetSeats: number }
    >({
      query: (body) => ({
        url: API_SUBSCRIPTION_SEATS_UPGRADE,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "getSeatStatus",
        "getCurrentPlan",
        "getSeatHistory",
        "getActivity",
        "getUpcomingInvoice",
      ],
    }),

    scheduleDowngrade: builder.mutation<
      IApiResponse<unknown>,
      { targetSeats: number }
    >({
      query: (body) => ({
        url: API_SUBSCRIPTION_SEATS_SCHEDULE_DOWNGRADE,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "getSeatStatus",
        "getSeatHistory",
        "getActivity",
        "getUpcomingInvoice",
      ],
    }),

    cancelScheduledDowngrade: builder.mutation<IApiResponse<unknown>, void>({
      query: () => ({
        url: API_SUBSCRIPTION_SEATS_CANCEL_SCHEDULED,
        method: "DELETE",
      }),
      invalidatesTags: [
        "getSeatStatus",
        "getSeatHistory",
        "getActivity",
        "getUpcomingInvoice",
      ],
    }),

    getAiActions: builder.query<IApiResponse<IAiAction[]>, void>({
      query: () => ({
        url: API_GET_AI_ACTIONS,
        method: "GET",
      }),
    }),

    getTokenUsageBreakdown: builder.query<
      IApiResponse<ITokenUsageBreakdown>,
      void
    >({
      query: () => ({
        url: API_SUBSCRIPTION_TOKEN_USAGE_BREAKDOWN,
        method: "GET",
      }),
      providesTags: ["getTokenUsageBreakdown"],
    }),

    getMyTimeSaved: builder.query<
      IApiResponse<IUserTimeSaved>,
      { period?: string } | void
    >({
      query: (params) => ({
        url: API_SUBSCRIPTION_MY_TIME_SAVED,
        method: "GET",
        params: params ?? {},
      }),
      providesTags: ["getMyTimeSaved"],
    }),

    getUsersTimeSaved: builder.query<
      IApiResponse<IUsersTimeSaved>,
      { period?: string; page?: number; limit?: number; search?: string } | void
    >({
      query: (params) => ({
        url: API_SUBSCRIPTION_USERS_TIME_SAVED,
        method: "GET",
        params: params ?? {},
      }),
      providesTags: ["getUsersTimeSaved"],
    }),

    // ── Activity / History ───────────────────────────────────────────────────

    getTokenPurchasesHistory: builder.query<
      IApiPaginatedResponse<ITokenPurchaseHistoryItem[]>,
      { page?: number; limit?: number; status?: string }
    >({
      query: (params) => ({
        url: API_SUBSCRIPTION_TOKEN_PURCHASES,
        method: "GET",
        params,
      }),
      providesTags: ["getTokenPurchases"],
    }),

    getSeatHistory: builder.query<
      IApiPaginatedResponse<ISeatHistoryItem[]>,
      { page?: number; limit?: number; status?: string }
    >({
      query: (params) => ({
        url: API_SUBSCRIPTION_SEAT_HISTORY,
        method: "GET",
        params,
      }),
      providesTags: ["getSeatHistory"],
    }),

    getPaymentHistory: builder.query<
      IApiPaginatedResponse<IPaymentHistoryItem[]>,
      { page?: number; limit?: number; status?: string; type?: string }
    >({
      query: (params) => ({
        url: API_SUBSCRIPTION_PAYMENT_HISTORY,
        method: "GET",
        params,
      }),
      providesTags: ["getPaymentHistory"],
    }),

    getActivity: builder.query<
      {
        success: boolean;
        data: IActivityItem[];
        meta: IActivityResponseMeta;
      },
      { page?: number; limit?: number; types?: ActivityEventType[] }
    >({
      query: (params) => ({
        url: API_SUBSCRIPTION_ACTIVITY,
        method: "GET",
        params: {
          page: params.page,
          limit: params.limit,
          types: params.types?.join(","),
        },
      }),
      providesTags: ["getActivity"],
    }),
  }),
});

export const {
  useGetSubscriptionPlanQuery,
  useGetSubscriptionCategoriesQuery,
  useGetSubscriptionFaqsQuery,
  useGetTopUpTokensQuery,
  useActivateSubscriptionMutation,
  useGetCurrentPlanQuery,
  useGetTokenSummaryQuery,
  useLazyExportTokenSummaryQuery,
  useGetTokenUsageQuery,
  useGetUpcomingInvoiceQuery,
  useCancelSubscriptionMutation,
  useGetSubscriptionInvoicesQuery,
  // Billing Address
  useGetBillingAddressQuery,
  useUpdateBillingAddressMutation,
  // Payment Methods
  useCreateSetupIntentMutation,
  useGetPaymentMethodsQuery,
  useSetDefaultPaymentMethodMutation,
  useRemovePaymentMethodMutation,
  // Tokens
  useGetTokenBalanceQuery,
  useCreateTopUpIntentMutation,
  useGetTopUpPackageByIdQuery,
  // Subscription lifecycle
  useResumeSubscriptionMutation,
  useRetryPaymentMutation,
  // Pre-purchase seats
  useGetSeatStatusQuery,
  useUpgradeSeatsMutation,
  useScheduleDowngradeMutation,
  useCancelScheduledDowngradeMutation,
  // AI actions catalog
  useGetAiActionsQuery,
  useGetTokenUsageBreakdownQuery,
  useGetMyTimeSavedQuery,
  useGetUsersTimeSavedQuery,
  // Activity / history
  useGetTokenPurchasesHistoryQuery,
  useGetSeatHistoryQuery,
  useGetPaymentHistoryQuery,
  useGetActivityQuery,
} = subscriptionApi;
